import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
    ArrowLeft, 
    FileCode2,
    Puzzle,
    Calendar,
    Play,
    AlertCircle,
    CheckCircle2,
    Loader2,
    Info,
    Tag
} from 'lucide-react';
import { SiteHeader } from '@/components/site-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { FadeId } from '@/components/ui/fade-id';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from '@/lib/utils';
import { 
    getTemplateById, 
    createFlowFromTemplate,
    getCurrentProjectId
} from '@/api/templates';
import type { Template } from '@/api/templates';
import { formatPieceDisplayName, getUniquePieces } from '@/api/templates/templates.utils';

interface TemplateDetailsProps {
    templateId: string;
    onBack: () => void;
}

// StatButton component for consistent stats display
interface StatButtonProps {
    icon: React.ElementType;
    value: string | number;
    label: string;
    tooltip: string;
    active?: boolean;
}

const StatButton = ({ icon: Icon, value, label, tooltip, active = false }: StatButtonProps) => {
    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div
                        className={cn(
                            "h-11 flex flex-col items-center justify-center gap-0 px-2.5 py-2",
                            "hover:bg-muted/50 transition-colors cursor-default",
                            active && "bg-primary/5"
                        )}
                    >
                        <div className="flex items-center gap-1 mb-0.5">
                            <Icon className="h-3.5 w-3.5 text-primary" />
                            <span className="text-xs font-semibold">{value}</span>
                        </div>
                        <span className="text-[9px] text-muted-foreground uppercase tracking-wide leading-tight">{label}</span>
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                    <p className="text-xs">{tooltip}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export function TemplateDetails({ templateId, onBack }: TemplateDetailsProps) {
    const navigate = useNavigate();
    const enhancedToast = useEnhancedToast();
    
    const [template, setTemplate] = useState<Template | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isCreatingFlow, setIsCreatingFlow] = useState(false);

    const fetchTemplate = async () => {
        setIsLoading(true);
        try {
            const response = await getTemplateById(templateId);
            if (response.error) {
                enhancedToast.error({
                    icon: AlertCircle,
                    title: 'Failed to load template',
                    description: response.error
                });
                onBack();
            } else if (response.data) {
                setTemplate(response.data);
            }
        } catch (error) {
            enhancedToast.error({
                icon: AlertCircle,
                title: 'Error loading template',
                description: 'Failed to fetch template details'
            });
            onBack();
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchTemplate();
    }, [templateId]);

    const handleUseTemplate = async () => {
        if (!template) return;
        
        setIsCreatingFlow(true);
        try {
            // Get current project ID from SDK
            const projectId = await getCurrentProjectId();
            
            if (!projectId) {
                throw new Error('Could not determine project. Please refresh and try again.');
            }
            
            // Create flow from template
            const result = await createFlowFromTemplate(template, projectId);
            
            if (result.error || !result.data) {
                throw new Error(result.error || 'Failed to create flow from template');
            }
            
            enhancedToast.success({
                icon: CheckCircle2,
                title: 'Flow created successfully',
                description: `"${result.data.version.displayName}" has been created from template`
            });
            
            // Navigate to the flow builder
            navigate(`/dashboard/workflows/flows/${result.data.id}`);
            
        } catch (error) {
            console.error('Error creating flow from template:', error);
            enhancedToast.error({
                icon: AlertCircle,
                title: 'Failed to create flow',
                description: (error as Error).message || 'An error occurred while creating the flow'
            });
        } finally {
            setIsCreatingFlow(false);
        }
    };

    if (isLoading) {
        return (
            <>
                <SiteHeader 
                    title="Template Details"
                    subtitle="Loading template information..."
                    backButton={
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onBack}
                            className="gap-2"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back
                        </Button>
                    }
                >
                    <Skeleton className="h-9 w-32 rounded-md" />
                </SiteHeader>
                
                <div className="flex-1 overflow-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8 space-y-6"
                    >
                        {/* Overview Card Skeleton */}
                        <Card className="shadow-sm">
                            <CardHeader className="border-b bg-muted/20">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3 flex-1">
                                        <Skeleton className="h-10 w-10 rounded-lg" />
                                        <div className="flex-1 space-y-2">
                                            <Skeleton className="h-5 w-48" />
                                            <Skeleton className="h-4 w-72" />
                                        </div>
                                    </div>
                                    <Skeleton className="h-8 w-36 rounded-md" />
                                </div>
                            </CardHeader>
                            <CardContent className="pt-6">
                                <div className="rounded-lg border border-border overflow-hidden bg-muted/30">
                                    <div className="grid grid-cols-4 divide-x divide-border">
                                        {[...Array(4)].map((_, i) => (
                                            <div key={i} className="h-11 flex items-center justify-center">
                                                <Skeleton className="h-6 w-16" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Pieces & Flows Skeleton */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <Card className="shadow-sm">
                                <CardHeader className="border-b bg-muted/20">
                                    <div className="flex items-center gap-3">
                                        <Skeleton className="h-8 w-8 rounded-lg" />
                                        <div className="space-y-1">
                                            <Skeleton className="h-5 w-32" />
                                            <Skeleton className="h-4 w-48" />
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-6">
                                    <div className="flex flex-wrap gap-2">
                                        {[...Array(5)].map((_, i) => (
                                            <Skeleton key={i} className="h-9 w-24 rounded-lg" />
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="shadow-sm">
                                <CardHeader className="border-b bg-muted/20">
                                    <div className="flex items-center gap-3">
                                        <Skeleton className="h-8 w-8 rounded-lg" />
                                        <div className="space-y-1">
                                            <Skeleton className="h-5 w-32" />
                                            <Skeleton className="h-4 w-48" />
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-6">
                                    <Skeleton className="h-20 w-full rounded-lg" />
                                </CardContent>
                            </Card>
                        </div>
                    </motion.div>
                </div>
            </>
        );
    }

    if (!template) {
        return null;
    }

    const uniquePieces = getUniquePieces(template.pieces);

    return (
        <>
            <SiteHeader 
                title="Template Details"
                subtitle="Review and use this workflow template"
                backButton={
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onBack}
                        className="gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                    </Button>
                }
            >
                {/* Template ID */}
                <FadeId 
                    id={templateId} 
                    label="Template ID"
                    tooltipSide="bottom"
                    height="h-9"
                />

                <Button
                    onClick={handleUseTemplate}
                    disabled={isCreatingFlow}
                    size="sm"
                >
                    {isCreatingFlow ? (
                        <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Creating...
                        </>
                    ) : (
                        <>
                            <Play className="h-4 w-4 mr-2" />
                            Use Template
                        </>
                    )}
                </Button>
            </SiteHeader>
            
            <div className="flex-1 overflow-auto">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-4 py-6 md:px-8 md:py-8 space-y-6"
                >
                    {/* Overview Card */}
                    <Card className="shadow-sm">
                        <CardHeader className="border-b bg-muted/20">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary/10 via-background to-primary/10 ring-1 ring-primary/10 shadow-sm flex-shrink-0">
                                        <FileCode2 className="h-5 w-5 text-primary" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <CardTitle className="text-lg truncate">{template.name}</CardTitle>
                                        <CardDescription className="mt-1 line-clamp-2">
                                            {template.summary || template.description}
                                        </CardDescription>
                                    </div>
                                </div>

                                {/* Tags */}
                                {template.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 justify-end flex-shrink-0 max-w-[200px]">
                                        {template.tags.map((tag, index) => (
                                            <Badge 
                                                key={index} 
                                                variant="secondary"
                                                className="text-xs px-2 py-0.5"
                                                style={{ 
                                                    backgroundColor: tag.color ? `${tag.color}20` : undefined,
                                                    borderColor: tag.color || undefined
                                                }}
                                            >
                                                {tag.title}
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent className="pt-6">
                            {/* Description if different from summary */}
                            {template.description && template.description !== template.summary && (
                                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                                    {template.description}
                                </p>
                            )}

                            {/* Stats Grid */}
                            <div className="rounded-lg border border-border overflow-hidden bg-muted/30">
                                <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
                                    <StatButton
                                        icon={Puzzle}
                                        value={uniquePieces.length}
                                        label="Pieces"
                                        tooltip={`${uniquePieces.length} unique piece${uniquePieces.length === 1 ? '' : 's'} used`}
                                        active={uniquePieces.length > 0}
                                    />
                                    <StatButton
                                        icon={Tag}
                                        value={template.tags.length}
                                        label="Tags"
                                        tooltip={template.tags.length > 0 
                                            ? `Tags: ${template.tags.map(t => t.title).join(', ')}`
                                            : 'No tags'
                                        }
                                        active={template.tags.length > 0}
                                    />
                                    <StatButton
                                        icon={Calendar}
                                        value={new Date(template.created).toLocaleDateString()}
                                        label="Created"
                                        tooltip={`Created on ${new Date(template.created).toLocaleDateString()}`}
                                    />
                                    <StatButton
                                        icon={Calendar}
                                        value={new Date(template.updated).toLocaleDateString()}
                                        label="Updated"
                                        tooltip={`Last updated on ${new Date(template.updated).toLocaleDateString()}`}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Used Pieces Card */}
                    {uniquePieces.length > 0 && (
                        <Card className="shadow-sm">
                            <CardHeader className="border-b bg-muted/20 py-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary/10 via-background to-primary/10 ring-1 ring-primary/10 shadow-sm">
                                        <Puzzle className="h-4 w-4 text-primary" />
                                    </div>
                                    <div>
                                        <CardTitle className="text-base">Used Pieces ({uniquePieces.length})</CardTitle>
                                        <CardDescription className="text-xs">Integrations used in this template</CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="pt-4">
                                <div className="flex flex-wrap gap-2">
                                    {uniquePieces.map((piece, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-md border border-border text-sm"
                                        >
                                            <Puzzle className="h-3.5 w-3.5 text-primary" />
                                            <span className="font-medium">
                                                {formatPieceDisplayName(piece)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Info Note */}
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/30 border border-border">
                        <Info className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-muted-foreground">
                            Clicking <span className="font-medium text-foreground">"Use Template"</span> will create a new flow based on this template. You can then customize it according to your needs.
                        </p>
                    </div>
                </motion.div>
            </div>
        </>
    );
}
